"use client";

import {
  ArrowLeft,
  CheckCircle2,
  LoaderCircle,
  Mail,
  ShieldCheck,
} from "lucide-react";
import Link from "next/link";
import {
  FormEvent,
  useState,
} from "react";

import Logo from "@/components/Logo";
import { requestPasswordReset } from "@/lib/account";
import { ApiError } from "@/lib/api";


function getErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    return error.message;
  }

  return (
    "Não foi possível solicitar a recuperação. "
    + "Tente novamente."
  );
}


export default function RecoverPasswordForm() {
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] =
    useState(false);
  const [successMessage, setSuccessMessage] =
    useState("");
  const [errorMessage, setErrorMessage] =
    useState("");

  async function handleSubmit(
    event: FormEvent<HTMLFormElement>,
  ) {
    event.preventDefault();

    if (isSubmitting) {
      return;
    }

    const normalizedEmail = email
      .trim()
      .toLowerCase();

    if (!normalizedEmail) {
      setErrorMessage("Informe seu e-mail.");
      return;
    }

    setErrorMessage("");
    setSuccessMessage("");
    setIsSubmitting(true);

    try {
      const response = await requestPasswordReset({
        email: normalizedEmail,
      });

      setEmail(normalizedEmail);
      setSuccessMessage(response.detail);
    } catch (error) {
      setErrorMessage(getErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen w-full bg-slate-50">
      <section className="flex w-full items-center justify-center px-5 py-8 lg:w-1/2 lg:px-12">
        <div className="w-full max-w-[430px]">
          <Link
            href="/login"
            className="inline-flex items-center gap-2 text-sm font-medium text-slate-500 transition-colors hover:text-blue-600"
          >
            <ArrowLeft size={17} />
            Voltar para o login
          </Link>

          <div className="mt-7">
            <Logo compact />
          </div>

          <div className="mt-8">
            {successMessage ? (
              <>
                <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-600">
                  <CheckCircle2 size={32} />
                </div>

                <h1 className="mt-6 text-3xl font-bold tracking-tight text-slate-950">
                  Confira seu e-mail
                </h1>

                <p className="mt-3 text-base leading-7 text-slate-500">
                  {successMessage}
                </p>

                <div className="mt-5 rounded-2xl border border-slate-200 bg-white px-4 py-4 text-sm leading-6 text-slate-500">
                  Confira também as pastas de spam,
                  lixo eletrônico e promoções.
                </div>

                <button
                  type="button"
                  onClick={() => {
                    setSuccessMessage("");
                    setErrorMessage("");
                  }}
                  className="mt-6 inline-flex h-12 w-full items-center justify-center rounded-2xl border border-slate-300 bg-white text-sm font-bold text-slate-700 transition-colors hover:border-blue-300 hover:text-blue-600"
                >
                  Solicitar novamente
                </button>
              </>
            ) : (
              <>
                <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-blue-50 text-blue-600">
                  <Mail size={31} />
                </div>

                <h1 className="mt-6 text-3xl font-bold tracking-tight text-slate-950">
                  Esqueceu sua senha?
                </h1>

                <p className="mt-3 text-base leading-7 text-slate-500">
                  Informe o e-mail cadastrado para
                  receber um link de redefinição.
                </p>

                <form
                  onSubmit={handleSubmit}
                  className="mt-7"
                >
                  <label
                    htmlFor="recovery-email"
                    className="text-sm font-semibold text-slate-800"
                  >
                    E-mail
                  </label>

                  <div className="mt-2 flex h-12 items-center gap-3 rounded-2xl border border-slate-300 bg-white px-4 focus-within:border-blue-500 focus-within:ring-4 focus-within:ring-blue-100">
                    <Mail
                      size={18}
                      className="text-slate-400"
                    />

                    <input
                      id="recovery-email"
                      type="email"
                      value={email}
                      onChange={(event) =>
                        setEmail(event.target.value)
                      }
                      placeholder="seu@email.com"
                      autoComplete="email"
                      disabled={isSubmitting}
                      required
                      className="h-full min-w-0 flex-1 bg-transparent text-sm text-slate-900 outline-none placeholder:text-slate-400 disabled:cursor-not-allowed"
                    />
                  </div>

                  {errorMessage && (
                    <div
                      role="alert"
                      className="mt-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700"
                    >
                      {errorMessage}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="mt-6 inline-flex h-12 w-full items-center justify-center gap-2 rounded-2xl bg-blue-600 text-sm font-bold text-white shadow-[0_12px_30px_rgba(37,99,235,0.25)] transition-all hover:-translate-y-0.5 hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-70 disabled:hover:translate-y-0"
                  >
                    {isSubmitting ? (
                      <>
                        <LoaderCircle
                          size={18}
                          className="animate-spin"
                        />
                        Enviando...
                      </>
                    ) : (
                      "Enviar link de recuperação"
                    )}
                  </button>
                </form>
              </>
            )}
          </div>

          <p className="mt-6 text-center text-sm text-slate-500">
            Lembrou sua senha?{" "}
            <Link
              href="/login"
              className="font-semibold text-blue-600 hover:text-blue-700"
            >
              Entre aqui
            </Link>
          </p>
        </div>
      </section>

      <aside className="relative hidden min-h-screen w-1/2 overflow-hidden bg-gradient-to-br from-blue-700 via-blue-600 to-sky-400 lg:flex lg:items-center lg:justify-center">
        <div className="absolute -right-24 -top-24 h-80 w-80 rounded-full bg-white/10 blur-3xl" />
        <div className="absolute -bottom-28 -left-20 h-96 w-96 rounded-full bg-cyan-300/20 blur-3xl" />

        <div className="relative z-10 max-w-lg px-12 text-center text-white">
          <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-3xl bg-white/15 backdrop-blur-sm">
            <ShieldCheck size={36} />
          </div>

          <h2 className="mt-8 text-4xl font-bold tracking-tight">
            Recupere seu acesso com segurança
          </h2>

          <p className="mt-5 text-lg leading-8 text-blue-50">
            O link é temporário e poderá ser
            utilizado apenas uma vez.
          </p>
        </div>
      </aside>
    </div>
  );
}
